import React, { memo, useState } from 'react';
import * as mobxReact from 'mobx-react';
import * as IoC from 'typescript-ioc';
import { isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import classnames from 'classnames';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';

import { MeetingStatus } from '../../../../../api/meetings.dao';
import { MeetingProgressBar } from '../../shared/progress-bar/meeting-progress-bar';
import { FormField } from '../../../../global/form/form-field/form-field';
import { EditableArea } from '../../../../global/editable-area/editable-area';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { DateFnsFormats } from '../../../../../infrastructure/date-formatter';
import { getFormattedTimezoneOffset } from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { OperatorsList } from '../../shared/operators-list/operators-list';
import { AttendeesSection } from '../../shared/attendees-section/attendees-section';
import { AttendeeRole } from '../../../../../api/attendees.dao';
import { MeetingProgress } from '../../../../../api/control.dao';
import OENotification, { ResponseCode } from '../../../../global/oe-notification/oe-notification';
import { CellHeaderButton, ListLayout } from '../../../../global/tables/table-list-layout';
import { ButtonType, SearchInput } from '../../../../global/search-input/search-input';
import { ControlFormConnections, ControlFormListOfConnections } from './_control-form-connections';
import { ZoomMeetingModal } from '../../shared/connections-list/_zoom-meeting-modal/zoom-meeting-modal.presentation';
import AttendeeAndOrganizationDropdown from './attendee-and-organization-dropdown';
import MeetingGuidelines from '../../../../global/meeting-guidelines/meeting-guidelines';
import { OperatorSharedScreenDropdown } from './_operator-shared-screen-dropdown/_operator-shared-screen-dropdown';

import './_control-form.scss';
import { getCompleteRoomName } from '../../../../../infrastructure/utils/common.utils';
import Text from '../../../../global/text/text';
import { IUserOperatorDTO } from '../../../../../api/operators.dao';
import { ControlFormVM } from './_control-form.viewmodel';
import { ConnectionType, IConnectionDetailsDTO } from '../../../../../api/connections.dao';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../../tracking-service/tracking-values';
import { CreateZoomWebinarModal } from '../../shared/connections-list/_create-zoom-webinar-modal/create-zoom-webinar-modal.presentation';
import { MeetingApiParams } from '../../shared/connections-list/_connection-form/connection-form.viewmodel';
import { getSortedConnections } from '../../../../../infrastructure/utils/connections.utils';
import PodiumLinkSection from '../../../../global/podium-link-popover/podium-link-section';

interface IAttendeeSection {
    name: string;
    key: string;
    id: string;
    role: AttendeeRole;
    hideIfEmpty?: boolean;
}

interface SearchAttendeesProps {
    onChangeFilter: (value: string) => void;
    placeholder: string;
    filterValue: string;
}

interface IProps {
    vm: ControlFormVM;
    intl: IntlShape;
    isExpanded?: boolean;
    ActionButton?: React.FunctionComponent<{ role: AttendeeRole }>;
    setMeetingStartDate?: () => void;
}

enum AttendeeFilterValues {
    Organization = 'Organization',
    Attendee = 'Attendee'
}

const SearchAttendees = memo(({ onChangeFilter, placeholder, filterValue }: SearchAttendeesProps) => (
    <div className="control-form__search-input-container">
        <SearchInput
            placeholder={placeholder}
            onChange={(e, value) => onChangeFilter(value)}
            resetSearch={() => onChangeFilter('')}
            className="w-100 control-form__search-input"
            buttonType={ButtonType.Black}
            value={filterValue}
            withResetSearchButton
        />
    </div>
));

const TimeAndProgress = props => {
    const {
        isEventTimezoneTimeDifferents,
        dayStringInEventTimezone,
        startTimeInEventTimezone,
        endTimeInEventTimezone,
        isMeetingCancelled,
        getEventTimeZone,
        localDayString,
        timeZoneOffset,
        startDateTime,
        startTime,
        progress,
        endTime,
        vm,
    } = props;

    return <div>
        <div className={classnames('control-form__date', {
            'control-form__date--cancelled': isMeetingCancelled
        })}
        >
            <span className="control-form__event-day">
                {dayStringInEventTimezone}
            </span>
            <div className="control-form__event-time">
                {startTimeInEventTimezone} - {endTimeInEventTimezone}
            </div>
            {!isSameDay(
                startDateTime,
                utcToZonedTime(startDateTime, getEventTimeZone())
            ) && (
                    <div
                        className="control-form__local-day"
                        data-testid="local-day"
                    >
                        {localDayString}
                    </div>
                )}
            {isEventTimezoneTimeDifferents && (
                <div
                    className="control-form__local-time"
                    data-testid="local-time"
                >
                    {startTime} - {endTime} ({timeZoneOffset})
                </div>
            )}
        </div>
        <MeetingProgressBar
            updateMeetingData={vm.updateMeetingProgress}
            meetingProgress={progress}
            disabledSteps={isMeetingCancelled ? [MeetingProgress.ENDED] : []}
        />
    </div>;
};

const ControlFormBody = injectIntl(
    mobxReact.observer((props: IProps) => {
        const [filterValue, setFilterValue] = useState('');
        const { vm, intl, isExpanded = false, ActionButton, setMeetingStartDate } = props;
        const formData = vm.getControlFormDataVM();

        const sections: IAttendeeSection[] = [
            {
                name: 'Presenters',
                id: 'oe.common.presenters',
                key: 'selectedPresenters',
                role: AttendeeRole.Presenter
            },
            {
                name: 'Participants',
                id: 'oe.common.participants',
                key: 'selectedParticipants',
                role: AttendeeRole.Participant
            },
            {
                name: 'Hosts',
                id: 'oe.common.hosts',
                key: 'selectedHosts',
                role: AttendeeRole.Host
            },
            {
                name: 'Supports',
                id: 'oe.common.supports',
                key: 'selectedSupports',
                role: AttendeeRole.Support,
                hideIfEmpty: true
            }
        ];
        const [selectedAttendeeFilter, setAttendeeFilter] = useState(AttendeeFilterValues.Organization);

        const { startDateTime, endDateTime, operators } = formData;
        const {
            progress,
            room,
            meetingRecordEnabled,
            operatorSharedScreen,
            updateOperatorSharedScreen,
            getEventTimeZone,
            getDisabledAttendees,
            setFilterCriteria,
            fetchAttendeeData,
            attendees,
            status,
            connections,
            attendeeIdToConnectionNameMap,
            orgIdToConnectionParticipantsNameMap,
            orgIdToConnectionPresentersNameMap,
            orgIdToConnectionHostsNameMap,
            orgIdToConnectionSupportsNameMap,
            onEditConnection,
            onDeleteConnection,
            defaultConnectionId,
            viewEventGuidelines,
            isEventNotesExist,
            guidelinesFormSubWindow,
            meetingConnectionVersion,
            loadingAttendees,
            resetLoadingState,
        } = vm;

        const filteredAttendees = vm.filterCriteria ? sections.map(({ key }) => vm.getFiltered(vm[key])) : [];
        const isMeetingCancelled = status === MeetingStatus.Cancelled;
        const eventTimezone = getEventTimeZone();
        const disabledAttendees = getDisabledAttendees();
        const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

        const startTime = dateTimeFormatter.dateToFormattedString(
            startDateTime,
            dateTimeFormatter.getLocalTimeFormat()
        );
        const endTime = dateTimeFormatter.dateToFormattedString(endDateTime, dateTimeFormatter.getLocalTimeFormat());

        const dayStringInEventTimezone = dateTimeFormatter.dateToFormattedString(
            startDateTime,
            DateFnsFormats.StringifiedFullDayMonthNumericDateYear,
            eventTimezone
        );

        const startTimeInEventTimezone = dateTimeFormatter.dateToFormattedString(
            startDateTime,
            dateTimeFormatter.getLocalTimeFormat(),
            eventTimezone
        );

        const endTimeInEventTimezone = dateTimeFormatter.dateToFormattedString(
            endDateTime,
            dateTimeFormatter.getLocalTimeFormat(),
            eventTimezone
        );

        const timeZoneOffset = getFormattedTimezoneOffset(startDateTime);

        const isEventTimezoneTimeDifferents = startTimeInEventTimezone !== startTime;

        const localDayString = dateTimeFormatter.dateToFormattedString(
            startDateTime,
            DateFnsFormats.StringifiedFullDayMonthNumericDateYear
        );

        const onChangeFilter = (value: string) => {
            setFilterValue(value);
            setFilterCriteria(value);
        };

        const attendeeTheads = [
            <AttendeeAndOrganizationDropdown
                selectedAttendeeFilter={selectedAttendeeFilter}
                setAttendeeFilter={setAttendeeFilter}
            />,
            intl.formatMessage(
                {
                    id: 'oe.attendee.av',
                    description: 'Table header',
                    defaultMessage: 'AV',
                }
            ),
            intl.formatMessage({
                id: 'oe.common.issues',
                description: 'Table header',
                defaultMessage: 'Issues',
            }
            ),
            intl.formatMessage(
                {
                    id: 'oe.common.tableConnection',
                    description: 'Table header',
                    defaultMessage: 'Connection'
                }),
            intl.formatMessage({
                id: 'oe.common.phone',
                description: 'Table header',
                defaultMessage: 'Phone'
            }),
            intl.formatMessage({
                id: 'oe.common.title',
                description: 'Label text',
                defaultMessage: 'Title'
            }),
            <div className="control-form__table-search-column">
                <CellHeaderButton
                    cellContent={intl.formatMessage(
                        {
                            id: 'oe.common.notes',
                            description: 'Label text',
                            defaultMessage: '{prefix}Notes'
                        },
                        {
                            prefix: ''
                        }
                    )}
                />
                <SearchAttendees
                    filterValue={filterValue}
                    onChangeFilter={onChangeFilter}
                    placeholder={intl.formatMessage({
                        id: 'oe.organization.searchOrganization',
                        description: 'Organization search input placeholder',
                        defaultMessage: 'Search'
                    })}
                />
            </div>
        ];

        const roomTheads = [
            intl.formatMessage({
                id: 'oe.common.connections',
                description: 'Label text',
                defaultMessage: 'Connections'
            }),
            intl.formatMessage({
                id: 'oe.common.connectionLink',
                description: 'Table header',
                defaultMessage: 'Connection Link'
            }),
            intl.formatMessage({
                id: 'oe.common.id',
                description: 'Label text',
                defaultMessage: 'ID'
            }),
            intl.formatMessage({
                id: 'oe.common.password',
                description: 'Password title',
                defaultMessage: 'Password'
            }),
            intl.formatMessage({
                id: 'oe.common.account',
                description: 'Table header',
                defaultMessage: 'Account'
            }),
            intl.formatMessage({
                id: 'oe.common.sip',
                description: 'Table header',
                defaultMessage: 'SIP'
            }),
            intl.formatMessage({
                id: 'oe.common.connectionNotes',
                description: 'Table header',
                defaultMessage: 'Connection Notes'
            }),
            intl.formatMessage({
                id: 'oe.common.dial',
                description: 'Table header',
                defaultMessage: 'Dial'
            })
        ];

        const completeRoomName = getCompleteRoomName({ name: room.roomName, label: room.roomLabel });

        const meetingParams: MeetingApiParams = {
            accountId: vm.accountId,
            eventId: vm.eventId,
            meetingId: vm.meetingId,
        };

        return (
            <div className={classnames('oe-form control-form popup-form', {
                'control-form--expanded': isExpanded
            })}>
                {isMeetingCancelled && <OENotification popupProps={{
                    message: <FormattedMessage
                        id="oe.meetings.meetingCancelled"
                        description="Meeting status notification"
                        defaultMessage="Meeting Cancelled"
                    />,
                    statusCode: ResponseCode.warning,
                    withFade: false
                }} />}
                <div className="control-form__date-and-progress">
                    {isExpanded ? <div>
                        <TimeAndProgress
                            isEventTimezoneTimeDifferents={isEventTimezoneTimeDifferents}
                            dayStringInEventTimezone={dayStringInEventTimezone}
                            startTimeInEventTimezone={startTimeInEventTimezone}
                            endTimeInEventTimezone={endTimeInEventTimezone}
                            isMeetingCancelled={isMeetingCancelled}
                            getEventTimeZone={getEventTimeZone}
                            localDayString={localDayString}
                            timeZoneOffset={timeZoneOffset}
                            startDateTime={startDateTime}
                            startTime={startTime}
                            progress={progress}
                            endTime={endTime}
                            vm={vm}
                        />
                        <FormField
                            className="oe-form-field__operators"
                            label={
                                <FormattedMessage
                                    id="oe.common.operators"
                                    defaultMessage="Operators"
                                    description="Label text"
                                />
                            }
                        >
                            {/* ROOM OPERATORS */}
                            <OperatorsList
                                operators={room?.operators as IUserOperatorDTO[] || []}
                                onOperatorStatusChange={email => vm.updateOperatorStatus(email)}
                                canChangeOperatorStatus
                                displayOperatorRole
                            />
                            {/* MEETING OPERATORS */}
                            {!!operators && <OperatorsList
                                operators={operators as IUserOperatorDTO[]}
                                onOperatorStatusChange={email => vm.updateMeetingOperatorStatus(email)}
                                canChangeOperatorStatus
                                displayOperatorRole
                            />}
                        </FormField>
                    </div>
                        : <TimeAndProgress
                            isEventTimezoneTimeDifferents={isEventTimezoneTimeDifferents}
                            dayStringInEventTimezone={dayStringInEventTimezone}
                            startTimeInEventTimezone={startTimeInEventTimezone}
                            endTimeInEventTimezone={endTimeInEventTimezone}
                            isMeetingCancelled={isMeetingCancelled}
                            getEventTimeZone={getEventTimeZone}
                            localDayString={localDayString}
                            timeZoneOffset={timeZoneOffset}
                            startDateTime={startDateTime}
                            startTime={startTime}
                            progress={progress}
                            endTime={endTime}
                            vm={vm}
                        />}
                    {!isExpanded &&
                        <FormField
                            className="oe-form-field__operators ordered-last"
                            label={
                                <FormattedMessage
                                    id="oe.common.operators"
                                    defaultMessage="Operators"
                                    description="Label text"
                                />
                            }
                        >
                            {/* ROOM OPERATORS */}
                            <OperatorsList
                                operators={room?.operators as IUserOperatorDTO[] || []}
                                onOperatorStatusChange={id => vm.updateOperatorStatus(id)}
                                canChangeOperatorStatus
                                displayOperatorRole
                            />
                            {/* MEETING OPERATORS */}
                            {!!operators && <OperatorsList
                                operators={operators as IUserOperatorDTO[]}
                                onOperatorStatusChange={id => vm.updateMeetingOperatorStatus(id)}
                                canChangeOperatorStatus
                                displayOperatorRole
                            />}
                        </FormField>
                    }
                    <div className='control-form__record-info'>
                        <MeetingGuidelines
                            meetingRecordEnabled={meetingRecordEnabled}
                            content={formData.meetingConnectionNotes}
                            isExpanded={isExpanded}
                            showEventNotesPopup={viewEventGuidelines}
                            isEventNotesExist={isEventNotesExist}
                            guidelinesFormSubWindow={guidelinesFormSubWindow}
                        />
                        <OperatorSharedScreenDropdown
                            value={operatorSharedScreen}
                            onChange={updateOperatorSharedScreen}
                        />
                    </div>
                </div>
                {isExpanded ? (
                    <div className="room-table">
                        <ListLayout thead={roomTheads} />
                        {vm.podiumLinkEnabled &&
                            <PodiumLinkSection
                                isExpanded={true}
                                getPodiumLink={vm.getPodiumLink}
                                handlePodiumLinkFailure={vm.handlePodiumLinkFailure}
                                meetingParams={{
                                    accountId: vm.accountId,
                                    eventId: vm.eventId,
                                    meetingId: vm.meetingId
                                }}
                                onCopy={() => vm.trackCopyPodiumLinkAction('Run Meeting Large', vm.meetingId)}
                            />
                        }
                        <div className="control-form__room">
                            <Text className="control-form__room-name" message={completeRoomName} withEllipsis />
                        </div>
                        <ControlFormConnections
                            eventTimezone={eventTimezone}
                            meetingConnections={getSortedConnections(connections)}
                            roomConnections={room?.connections}
                            onEditConnection={onEditConnection}
                            showConfigurePassportForm={(connection: IConnectionDetailsDTO)=> {
                                vm.showConfigurePassportForm({
                                    accountId: vm.accountId,
                                    eventId: vm.eventId,
                                    roomId: room.roomId,
                                    meetingConnectionId: connection.meetingConnectionId,
                                }, connection);
                            }}
                            onDeleteConnection={onDeleteConnection}
                            defaultRoomConnectionId={room.defaultConnection?.meetingConnectionId}
                            defaultMeetingConnectionId={defaultConnectionId}
                            onMeetingConnectionStatusChange={id => vm.updateMeetingConnectionStatus(id)}
                            onRoomConnectionStatusChange={id => vm.updateRoomConnectionStatus(id)}
                            webinarReportsVM={vm.getWebinarReportsVM()}
                            cloneConnection={vm.handleCloneConnection}
                            handleTroubleshoot={vm.handleTroubleshoot}
                            trackHostJoinClick={vm.trackHostJoinClick}
                            meetingParams={meetingParams}
                            shouldDisableDeleteButton={vm.shouldDisableDeleteButton}
                        />
                    </div>
                ) : (
                    <FormField
                        className="oe-form-field__connection"
                        label={
                            <FormattedMessage
                                id="oe.common.connections"
                                defaultMessage="Connections"
                                description="Label text"
                            />
                        }
                    >
                        {vm.podiumLinkEnabled &&
                            <PodiumLinkSection
                                getPodiumLink={vm.getPodiumLink}
                                handlePodiumLinkFailure={vm.handlePodiumLinkFailure}
                                meetingParams={{
                                    accountId: vm.accountId,
                                    eventId: vm.eventId,
                                    meetingId: vm.meetingId
                                }}
                                onCopy={() => vm.trackCopyPodiumLinkAction('Run Meeting Small', vm.meetingId)}
                            />
                        }
                        <div className="control-form__room">
                            <Text className="control-form__room-name" message={completeRoomName} withEllipsis />
                        </div>
                        <div className="connection-list m-bottom-0px">
                            {room?.connections?.length > 0 && (
                                <ControlFormListOfConnections
                                    connections={room.connections}
                                    onChange={onEditConnection}
                                    showConfigurePassportForm={(connection: IConnectionDetailsDTO)=> {
                                        vm.showConfigurePassportForm({
                                            accountId: vm.accountId,
                                            eventId: vm.eventId,
                                            roomId: room.roomId,
                                            meetingConnectionId: connection.meetingConnectionId,
                                        }, connection);
                                    }}
                                    eventTimezone={eventTimezone}
                                    onDelete={onDeleteConnection}
                                    defaultConnectionId={room.defaultConnection?.meetingConnectionId}
                                    onConnectionStatusChange={id => vm.updateRoomConnectionStatus(id)}
                                    controlledCheckbox={true}
                                    checkbox={true}
                                    webinarReportsVM={vm.getWebinarReportsVM()}
                                    cloneConnection={vm.handleCloneConnection}
                                    handleTroubleshoot={vm.handleTroubleshoot}
                                    handleTrackingEvent={
                                        (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.CALL) => {
                                            vm.trackFormOpenEventMixPanel(eventName);
                                        }
                                    }
                                    trackHostJoinClick={vm.trackHostJoinClick}
                                    meetingParams={meetingParams}
                                    shouldDisableDeleteButton={vm.shouldDisableDeleteButton}
                                />
                            )}
                            {connections.length > 0 && (
                                <>
                                    <label className="oe-form-field__title">Meeting Connections</label>
                                    <ControlFormListOfConnections
                                        connections={getSortedConnections(connections)}
                                        eventTimezone={eventTimezone}
                                        onChange={connection => {
                                            if (connection.type === ConnectionType.Knovio && setMeetingStartDate) {
                                                setMeetingStartDate();
                                            }
                                            onEditConnection(connection);
                                        }}
                                        showConfigurePassportForm={(connection: IConnectionDetailsDTO)=> {
                                            vm.showConfigurePassportForm({
                                                accountId: vm.accountId,
                                                eventId: vm.eventId,
                                                meetingId: vm.meetingId,
                                                meetingConnectionId: connection.meetingConnectionId,
                                            }, connection);
                                        }}
                                        onDelete={onDeleteConnection}
                                        defaultConnectionId={defaultConnectionId}
                                        cloneConnection={vm.handleCloneConnection}
                                        onConnectionStatusChange={id => vm.updateMeetingConnectionStatus(id)}
                                        controlledCheckbox={true}
                                        checkbox={true}
                                        webinarReportsVM={vm.getWebinarReportsVM()}
                                        handleTroubleshoot={vm.handleTroubleshoot}
                                        trackHostJoinClick={vm.trackHostJoinClick}
                                        handleTrackingEvent={
                                            (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.CALL) => {
                                                vm.trackFormOpenEventMixPanel(eventName);
                                            }
                                        }
                                        meetingParams={meetingParams}
                                        shouldDisableDeleteButton={vm.shouldDisableDeleteButton}
                                        connectionVersion={meetingConnectionVersion}
                                        openFrom={OpenFrom.RunMeeting}
                                    />
                                </>
                            )}
                        </div>
                    </FormField>
                )}
                {
                    isExpanded && (
                        <div className="attendees-table">
                            <ListLayout thead={attendeeTheads} />
                        </div>
                    )
                }
                {
                    isExpanded || (
                        <SearchAttendees
                            filterValue={filterValue}
                            onChangeFilter={onChangeFilter}
                            placeholder={intl.formatMessage({
                                id: 'oe.meeting.searchAttendees',
                                description: 'Search input for Attendees',
                                defaultMessage: 'Search Attendees'
                            })}
                        />
                    )
                }
                {
                    vm.filterCriteria && filteredAttendees.every(attendee => !attendee.length) ? (
                        <FormattedMessage
                            id="oe.items.noMatchingSearchCriteria"
                            description="Label text"
                            defaultMessage="No items matching search criteria"
                        />
                    ) : (
                        sections.map(({ name, key, role, id, hideIfEmpty }, index) =>
                            (!hideIfEmpty || (hideIfEmpty && !!vm[key].length)) && (
                                <AttendeesSection
                                    key={key}
                                    roleKey={key}
                                    id={id}
                                    name={name}
                                    role={role}
                                    updateAttendee={vm.updateAttendee}
                                    addMeetingAttendees={vm.addWalkInAttendees}
                                    selected={vm.filterCriteria ? filteredAttendees[index] : vm[key]}
                                    attendees={attendees}
                                    disabled={disabledAttendees}
                                    fetchAttendeeData={fetchAttendeeData}
                                    eventTimezone={eventTimezone}
                                    isExpanded={isExpanded}
                                    isWalkIn={true}
                                    removeWalkIn={vm.removeWalkIn}
                                    ActionButton={() => <ActionButton role={role} />}
                                    attendeeIdToConnectionNameMap={attendeeIdToConnectionNameMap}
                                    orgIdToConnectionParticipantsNameMap={orgIdToConnectionParticipantsNameMap}
                                    orgIdToConnectionPresentersNameMap={orgIdToConnectionPresentersNameMap}
                                    orgIdToConnectionHostsNameMap={orgIdToConnectionHostsNameMap}
                                    orgIdToConnectionSupportsNameMap={orgIdToConnectionSupportsNameMap}
                                    onClick={async () => await vm.callFetchOrganizationsFromPopUp()}
                                    onEditAttendee={(attendeeId: number) => vm.handleEditAttendeeClick(attendeeId, OpenFrom.RunMeeting)}
                                    selectedAttendeeFilter={
                                        isExpanded && selectedAttendeeFilter === AttendeeFilterValues.Attendee
                                    }
                                    isAttendeeListSortedByFirstName={true}
                                    isMeetingUnderway={vm.isMeetingUnderway}
                                    handleTrackingEvent={vm.trackEventMixPanel}
                                    loadingAttendees={loadingAttendees}
                                    resetLoadingState={resetLoadingState}
                                />
                            ))
                    )
                }
                <FormField
                    label={
                        <FormattedMessage
                            id="oe.common.notes"
                            description="Label text"
                            defaultMessage="{prefix}Notes"
                            values={{
                                prefix: 'Operator Meeting '
                            }}
                        />
                    }
                    className="read-only-textarea__wrapper"
                >
                    <EditableArea
                        content={vm.getMeetingNote()}
                        onSave={vm.updateOperatorMeetingNotes}
                        validator={vm.getEditableTextareaErrorMessage}
                        shouldSaveOnBlur
                    />
                </FormField>
                <ZoomMeetingModal vm={vm.getZoomMeetingVM()} />
                <CreateZoomWebinarModal vm={vm.getWebinarMeetingVM()} />
            </div>
        );
    })
);

export { AttendeeFilterValues, ControlFormBody, SearchAttendees, TimeAndProgress };
